import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import classNames from '../../node_modules/classnames/index.js';
import styles from './IconButton.module.scss.js';
import { applyCommonProps } from '../utility/commonProps.js';
import { getTarget } from '../utility/getTarget.js';
import { onAnchorKeyDown } from '../utility/onAnchorKeyDown.js';
import { invariant } from '../utility/invariant.js';

var _excluded = ["a11yLabel", "children", "className", "size", "variant"],
  _excluded2 = ["href", "onClick", "onKeyDown", "target"];
var isAnchor = function isAnchor(props) {
  return 'href' in props;
};
var _IconButton = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _applyCommonProps = applyCommonProps(props, IconButton),
    a11yLabel = _applyCommonProps.a11yLabel,
    children = _applyCommonProps.children,
    className = _applyCommonProps.className,
    _applyCommonProps$siz = _applyCommonProps.size,
    size = _applyCommonProps$siz === void 0 ? 'small' : _applyCommonProps$siz,
    _applyCommonProps$var = _applyCommonProps.variant,
    variant = _applyCommonProps$var === void 0 ? 'secondary' : _applyCommonProps$var,
    rest = _objectWithoutProperties(_applyCommonProps, _excluded);
  invariant(!!a11yLabel, '`IconButton` accessibility violation. `IconButton` requires an `a11yLabel`.');
  var classes = classNames(styles.icon, styles.button, size === 'large' && styles.large, size === 'medium' && styles.medium, size === 'small' && styles.small, variant === 'primary' && styles.primary, variant === 'secondary' && styles.secondary, className);
  var content = /*#__PURE__*/React.isValidElement(children) ? /*#__PURE__*/React.cloneElement(children, {
    size: size
  }) : children;
  if (isAnchor(props)) {
    var _ref = rest,
      href = _ref.href,
      onClick = _ref.onClick,
      anchorOnKeyDown = _ref.onKeyDown,
      target = _ref.target,
      anchorProps = _objectWithoutProperties(_ref, _excluded2);
    var onKeyDown = onAnchorKeyDown({
      href: href,
      onClick: onClick,
      onKeyDown: anchorOnKeyDown
    });
    return /*#__PURE__*/React.createElement("a", _extends({
      "aria-label": a11yLabel,
      className: classes,
      href: href,
      onClick: onClick,
      onKeyDown: onKeyDown,
      ref: ref
    }, getTarget(target), anchorProps), content);
  }
  return /*#__PURE__*/React.createElement("button", _extends({
    "aria-label": a11yLabel,
    className: classes,
    ref: ref,
    type: "button"
  }, rest), content);
});
_IconButton.displayName = 'IconButton';

/**
 * Icon Buttons trigger actions and rely solely on icons to convey their purpose.
 *
 * - {@link https://livingdesign.walmart.com/components/icon-button/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/icon-button/react/ React documentation}
 *
 * ### Content
 *
 * The Icon Button accepts an icon to display through the `children` prop.
 *
 * ```tsx
 * import * as React from "react";
 * import { IconButton } from "@walmart-web/livingdesign-components";
 * import Icon from "@livingdesign/icons";
 *
 * <IconButton a11yLabel="icon">
 *   <Icon.Cart />
 * </IconButton>
 * ```
 *
 * ### Features
 *
 * #### Size
 *
 * The Icon Button comes in three sizes:
 *
 * - `small` (default)
 * - `medium`
 * - `large`
 *
 * Set the Icon Button's size using the `size` prop.
 *
 * ### Accessibility
 *
 * The Icon Button requires an accessible label. Set the accessible label through the `a11yLabel` prop.
 *
 * ### Example
 * ```tsx
 * import * as React from "react";
 * import { IconButton } from "@walmart-web/livingdesign-components";
 * import Icon from "@livingdesign/icons";
 *
 * <IconButton a11yLabel="icon" size="large">
 *   <Icon.Cart />
 * </IconButton>
 * ```
 */
var IconButton = _IconButton;

export { IconButton };
